import type {
  ComponentAppearance,
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
import {
  resolveVariantClasses,
  variantTokens,
} from 'src/modules/shared/styles/variant-classes';

export const triStateCheckboxSizeClasses: Record<
  ComponentSize,
  { button: string }
> = {
  '2xs': {
    button: 'h-4 w-4 rounded',
  },
  xs: {
    button: 'h-5 w-5 rounded-md',
  },
  sm: {
    button: 'h-7 w-7 rounded-md',
  },
  md: {
    button: 'h-9 w-9 rounded-lg',
  },
  lg: {
    button: 'h-10 w-10 rounded-xl',
  },
};

export const triStateCheckboxIconClasses: Record<ComponentSize, string> = {
  '2xs': 'h-2.5 w-2.5',
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-[18px] w-[18px]',
  lg: 'h-5 w-5',
};

const checkedStateClasses = (
  variant: ComponentVariant,
  appearance: ComponentAppearance
): string => {
  const tokens = variantTokens[variant];

  switch (appearance) {
    case 'filled':
      return [
        'border',
        tokens.border500,
        tokens.solid,
        'hover:brightness-95',
      ].join(' ');
    case 'outline':
      return [
        'border',
        tokens.border300,
        'bg-white',
        tokens.text700,
        'hover:bg-neutral-50',
        'hover:border-neutral-400',
      ].join(' ');
    case 'soft':
      return [
        resolveVariantClasses(variant, 'soft'),
        'hover:brightness-95',
      ].join(' ');
    case 'ghost':
    default:
      return [
        'border-transparent',
        'bg-transparent',
        tokens.text700,
        'hover:bg-neutral-100',
      ].join(' ');
  }
};

const uncheckedStateClasses: Record<ComponentAppearance, string> = {
  filled: 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800',
  outline:
    'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-400',
  soft: 'border-slate-100 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:border-slate-200',
  ghost:
    'border-transparent bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-700',
};

const indeterminateStateClasses: Record<ComponentAppearance, string> = {
  filled: 'border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200',
  outline:
    'border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:border-slate-400 hover:text-slate-600',
  soft: 'border-slate-100 bg-slate-50 text-slate-500 hover:bg-slate-100 hover:border-slate-200 hover:text-slate-600',
  ghost:
    'border-transparent bg-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-700',
};

export const triStateCheckboxStateClasses = (
  value: boolean | null,
  variant: ComponentVariant,
  appearance: ComponentAppearance
): string => {
  if (value === true) {
    return checkedStateClasses(variant, appearance);
  }

  if (value === false) {
    return uncheckedStateClasses[appearance];
  }

  return indeterminateStateClasses[appearance];
};

const triStateCheckboxBoxSizeClasses: Record<ComponentSize, string> = {
  '2xs': 'h-3 w-3',
  xs: 'h-4 w-4',
  sm: 'h-5 w-5',
  md: 'h-6 w-6',
  lg: 'h-7 w-7',
};

/**
 * Icon sizes for the checkbox-style box, matched to `triStateCheckboxBoxClasses`.
 */
export const triStateCheckboxBoxIconClasses: Record<ComponentSize, string> = {
  '2xs': 'h-2 w-2',
  xs: 'h-3 w-3',
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

/**
 * Classes for the checkbox-style box used next to a visible label. It follows
 * the shared `Checkbox` look: a filled box for yes, an outlined box with an
 * icon for no, and an empty outlined box when the value is not set.
 *
 * @param value - Current tri-state value.
 * @param variant - Color variant for the yes state.
 * @param size - Box size.
 * @returns Tailwind classes for the box.
 */
export const triStateCheckboxBoxClasses = (
  value: boolean | null,
  variant: ComponentVariant,
  size: ComponentSize
): string => {
  const base = [
    'inline-flex shrink-0 items-center justify-center rounded border-2 transition-colors duration-200',
    triStateCheckboxBoxSizeClasses[size],
  ];

  if (value === true) {
    const tokens = variantTokens[variant];
    return [...base, tokens.border500, tokens.solid].join(' ');
  }

  if (value === false) {
    return [
      ...base,
      'border-secondary-500 bg-white text-secondary-700 group-hover:border-secondary-700',
    ].join(' ');
  }

  return [
    ...base,
    'border-secondary-500 bg-white group-hover:border-secondary-700',
  ].join(' ');
};

/**
 * Label classes for each state of a labeled tri-state checkbox. A "no" value
 * strikes the label through so it reads differently from "not set". Every
 * state stays at WCAG 2.1 AA text contrast on white and on the hover surface.
 */
export const triStateCheckboxLabelClasses: Record<
  'checked' | 'unchecked' | 'indeterminate',
  string
> = {
  checked: 'min-w-0 text-neutral-900',
  unchecked: 'min-w-0 text-neutral-500 line-through decoration-neutral-500',
  indeterminate: 'min-w-0 text-neutral-700',
};
